'use client';

import React, { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { buildScopeFilter, CaptureError, extractPathAfterBulk, FetchWithVersionCheck, folderPathForEvidenceLockerDisplay, handleDownload, isMetaFile, stripUnwantedLeadingPathSegments } from '@/shared/functions';
import { CircleDashedIcon, FunnelIcon, FunnelXIcon, ListMagnifyingGlassIcon, XCircleIcon, FolderOpenIcon, LockersIcon, XIcon, FileIcon, ClipboardTextIcon, CheckIcon, PlusCircleIcon, MinusCircleIcon, ArrowClockwiseIcon, FilesIcon, EyeIcon, LightbulbFilamentIcon, CrosshairIcon, CloudIcon, QuestionMarkIcon, CloudCheckIcon, CloudWarningIcon, CaretDownIcon } from "@phosphor-icons/react";
import { useHotkeys } from 'react-hotkeys-hook';
import { useWindowSize } from '../shared/clientFunctions';
import { motion } from 'framer-motion';
import { AuthContext, FeaturesContext, FullScreenContext, StatsContext, WalkthruContext } from '../app/contexts';
import { Button } from "@heroui/button";
import { Tooltip } from '@heroui/tooltip';
import { Alert } from "@heroui/alert";
import { recordStat, removeMD5Hash, getMediaType } from '@/shared/functions';
import { useDebounce } from 'use-debounce';
import { MediaObject, ScopesObject, Source } from '@/shared/types';
import { fileTypeControllers, META_FILE_REGEX } from '@/shared/constants';
import { fileTypeIcons } from '@/shared/uiConstants';
import Walkthru from './walkThru';
import { Popover, PopoverTrigger, PopoverContent } from '@heroui/react';
import { PROVENANCE_STATUS_GROUPS } from '@/shared/constants';
import { SourceObject } from '@/shared/types';
import { SourceCard } from './sourceCard';
import { useMediaSourceShare } from './mediaSourceShare';
import { EventFromChat } from './answer-item';
import { Checkbox } from '@heroui/react';
import { PromptLibrary } from './prompt-library';
interface EvidenceObject {
  mediaType: string;
  url: string;
}
const getFileTypeIconsControllersIconsMap = () => {
  const fileTypeIconsControllers = Object.entries(fileTypeControllers);
  const iconMap: Record<string, React.ElementType> = {};
  fileTypeIconsControllers.forEach(fileTypeIconsController => {
    const iconType = fileTypeIconsController[0];
    fileTypeIconsController[1].forEach(fileType => {
      iconMap[fileType] = fileTypeIcons[iconType as keyof typeof fileTypeIcons];
    });
  });
  return iconMap;
};
export const isStatus = (status: string | undefined, group: keyof typeof PROVENANCE_STATUS_GROUPS): boolean => {
  try {
    if (!status || typeof status !== 'string') return false;
    const normalizedStatus = status.toLowerCase().trim();
    const groupStatuses = PROVENANCE_STATUS_GROUPS[group] || [];

    // Check for exact match first
    let isInGroup = groupStatuses.includes(normalizedStatus);

    // If no exact match, check for partial matches
    if (!isInGroup) {
      isInGroup = groupStatuses.some(expectedStatus => normalizedStatus.includes(expectedStatus) || expectedStatus.includes(normalizedStatus));
    }
    return isInGroup;
  } catch (error) {
    return false;
  }
};
export const getStatusColor = (status: string | undefined): string => {
  try {
    if (!status || typeof status !== 'string') return 'text-black/80';
    if (isStatus(status, 'SUCCESS')) return 'text-green-400';
    if (isStatus(status, 'ERROR')) return 'text-red-600';
    if (isStatus(status, 'INTERMEDIARY')) return 'text-yellow-600';
    return 'text-black/80';
  } catch (error) {
    return 'text-black/80';
  }
};
export const getStatusDescription = (status: string | undefined): string => {
  try {
    if (!status || typeof status !== 'string') return 'Unknown verification status';
    if (isStatus(status, 'SUCCESS')) return 'Verified and trusted source';
    if (isStatus(status, 'ERROR')) return 'Source verification failed or rejected';
    if (isStatus(status, 'INTERMEDIARY')) return 'Source verification in progress';
    return 'Unknown verification status';
  } catch (error) {
    return 'Unknown verification status';
  }
};
export const ProvenanceIcon = ({
  status,
  size = 20,
  className = ""
}: {
  status: string | undefined;
  size?: number;
  className?: string;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  try {
    if (!status || typeof status !== 'string') {
      // Unknown status - show QuestionMarkIcon centered inside CloudIcon
      return <div className="relative box-border h-[20px] w-[20px] min-w-[20px] min-h-[20px] max-h-[20px] max-w-[20px]" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
          <CloudIcon size={size - 3} weight={isHovered ? "fill" : "regular"} className="text-gray-500" />
          <QuestionMarkIcon size={11} weight="bold" className={`absolute top-[50%] left-[66%] transform -translate-x-1/2 -translate-y-1/2 ${isHovered ? 'text-white' : 'text-gray-500'}`} />
        </div>;
    }

    // Known status - show the appropriate icon
    const IconComponent = getStatusIcon(status);
    return <div className="flex items-center justify-center" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        <IconComponent size={size} weight={isHovered ? "fill" : "regular"} className={`${className}`} />
      </div>;
  } catch (error) {
    // Fallback to a safe default icon with hover support
    return <div className="flex items-center justify-center" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        <CloudIcon size={size} weight={isHovered ? "fill" : "regular"} className={`${className} text-gray-500`} />
      </div>;
  }
};
export const getStatusIcon = (status: string | undefined): React.ElementType => {
  try {
    if (!status || typeof status !== 'string') return ProvenanceIcon;
    if (isStatus(status, 'SUCCESS')) return CloudCheckIcon;
    if (isStatus(status, 'ERROR')) return CloudWarningIcon;
    if (isStatus(status, 'INTERMEDIARY')) return CloudIcon;
    return ProvenanceIcon;
  } catch (error) {
    return ProvenanceIcon;
  }
};
interface QuestionBarProps {
  answers: any[];
  caseId: string;
  userId: string;
  sessionId: string | null;
  onSubmit: (query: string, scopesAndFiles: ScopesObject, userId: string, sessionId: string | null, retry: boolean, retryCount: number, useDeepDive: boolean) => Promise<void>;
  onClearThread: () => void;
  isLoading?: boolean;
  isResetting?: boolean;
  setQuery: (query: string) => void;
  query: string;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  hasChatHistory: boolean;
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  collapseSources: () => void;
  scopesAndFiles: ScopesObject;
  setScopesAndFiles: (scopesAndFiles: ScopesObject) => void;
  showFolders: boolean;
  setShowFolders: React.Dispatch<React.SetStateAction<boolean>>;
  allFileTypes: boolean;
  setAllFileTypes: React.Dispatch<React.SetStateAction<boolean>>;
  activeFileTypes: Record<string, boolean>;
  setActiveFileTypes: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  evidenceFilter: string;
  addSourceToScope: (path: string) => void;
  addToTimeline: (event: EventFromChat) => void;
  featureFlagsProp?: {
    [key: string]: any;
  };
  triggerPromptLibrary?: number;
}
const CopyTooltipContent = ({
  fileOrScope,
  scopesAndFiles
}: {
  fileOrScope: string;
  scopesAndFiles: ScopesObject;
}) => {
  const [copied, setCopied] = useState(false);
  const isScope = scopesAndFiles?.scopes?.includes(fileOrScope) || scopesAndFiles?.excludedScopes?.includes(fileOrScope);
  const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    try {
      const extractedFileOrScope = extractPathAfterBulk(fileOrScope).truncatedFilePath;
      const textToCopy = extractedFileOrScope || fileOrScope || '';
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(textToCopy).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1000); // Reset after 1 second
        }).catch(error => {
          console.warn('Clipboard write failed:', error);
          // Fallback to older method
          fallbackCopyTextToClipboard(textToCopy);
        });
      } else {
        // Fallback for non-secure contexts
        fallbackCopyTextToClipboard(textToCopy);
      }
    } catch (error) {
      console.warn('Error copying to clipboard:', error);
    }
  };
  const fallbackCopyTextToClipboard = (text: string) => {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      textArea.remove();
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    } catch (error) {
      console.warn('Fallback copy failed:', error);
    }
  };
  const displayText = isScope ? fileOrScope : extractPathAfterBulk(fileOrScope, true).truncatedFilePath;
  return <>
      {displayText}
      <button className="btn-h p-0 m-0" onClick={e => {
      e.stopPropagation();
      handleCopy(e);
    }}>
        {copied ? <CheckIcon size={20} /> : <ClipboardTextIcon weight="duotone" size={20} />}
      </button>
    </>;
};
const DynamicIcon = ({
  icon: Icon,
  size,
  weight,
  className
}: {
  icon: React.ElementType;
  size: number;
  weight: string;
  className: string;
}) => {
  return <Icon size={size} weight={weight} className={className} data-sentry-element="Icon" data-sentry-component="DynamicIcon" data-sentry-source-file="question-bar.tsx" />;
};
const FilterButton = ({
  isActive,
  onClick,
  icon: Icon,
  label,
  variant = 'compact'
}: {
  isActive: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  icon: React.ElementType;
  label: string;
  variant?: 'compact' | 'expanded';
}) => {
  const isExpanded = variant === 'expanded';
  const baseStyles = isExpanded ? "box-border flex min-w-[70px] flex-col items-center justify-center gap-1 cursor-pointer border-[2px] border-solid rounded-[4px] p-2 transition-colors duration-200 shrink-0 sm:flex-1 outline-none focus:outline-none focus-visible:outline-none" : "box-border flex flex-col items-center justify-center gap-0 cursor-pointer border-[2px] border-solid rounded-sm px-1 py-0 transition-colors duration-200 min-w-[52px] outline-none focus:outline-none focus-visible:outline-none";
  const activeStyles = isExpanded ? "border-brand-blue-light bg-[#E7EFF9] text-brand-blue" : "border-brand-blue-light/40 bg-brand-blue-light/20 text-brand-blue-light shadow-sm shadow-gray-300/30";
  const inactiveStyles = isExpanded ? "border-[#D9D9D9] bg-transparent text-gray-500 hover:bg-brand-blue-light/10 hover:border-brand-blue-light/20 hover:text-brand-blue-light" : "border-gray-200/30 bg-transparent text-gray-500 hover:bg-brand-blue-light/10 hover:border-brand-blue-light/20 hover:text-brand-blue-light";
  return <button onClick={e => {
    e.stopPropagation();
    onClick(e);
  }} className={`${baseStyles} ${isActive ? activeStyles : inactiveStyles}`} data-sentry-component="FilterButton" data-sentry-source-file="question-bar.tsx">
      <Icon size={25} weight="light" data-sentry-element="Icon" data-sentry-source-file="question-bar.tsx" />
      {isExpanded ? <span className="text-[11px] text-center tracking-[-0.55px] whitespace-nowrap">{label}</span> : <span className="sm:block hidden text-[10px] opacity-70">{label}</span>}
    </button>;
};
const ScopeDropdown = ({
  selectedContentCategories,
  onToggleCategory
}: {
  selectedContentCategories: string[];
  onToggleCategory: (category: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const allTypes = Object.keys(fileTypeControllers);
  const selectedCount = selectedContentCategories.length;
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  return <div ref={dropdownRef} className="relative shrink-0" data-sentry-component="ScopeDropdown" data-sentry-source-file="question-bar.tsx">
      <button type="button" onClick={e => {
      e.preventDefault();
      e.stopPropagation();
      setIsOpen(!isOpen);
    }} className={`flex items-center justify-between gap-2 pl-4 pr-2 py-2 rounded-full border-[2px] border-solid border-brand-blue-light cursor-pointer w-[184px] transition-colors duration-200 ${isOpen ? 'bg-brand-blue-light/20' : 'bg-transparent hover:bg-brand-blue-light/10'}`}>
        <span className="text-[14px] text-black whitespace-nowrap tracking-[-0.42px]">
          {selectedCount > 0 ? `${selectedCount} File Type${selectedCount > 1 ? 's' : ''}` : 'Scope by File Types'}
        </span>
        <CaretDownIcon size={20} weight="regular" className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-sentry-element="CaretDownIcon" data-sentry-source-file="question-bar.tsx" />
      </button>
      {isOpen && <div className="absolute top-full mt-1 right-0 z-[60] bg-white border-[2px] border-solid border-brand-blue-light rounded-md shadow-lg w-[184px] max-h-[240px] overflow-y-auto custom-scrollbar3">
          {allTypes.map(type => {
        const Icon = fileTypeIcons[type as keyof typeof fileTypeIcons];
        return <label key={type} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-brand-blue-light/10 transition-colors duration-150" onClick={e => e.stopPropagation()}>
                <Checkbox type="checkbox" classNames={{
            base: 'min-w-[18px] max-w-[18px] min-h-[18px] max-h-[18px] mr-0',
            wrapper: 'min-w-[18px] max-w-[18px] min-h-[18px] max-h-[18px] mr-0'
          }} isSelected={selectedContentCategories.includes(type)} onValueChange={() => onToggleCategory(type)} />
                {Icon && <Icon size={18} weight="light" className="text-gray-500" />}
                <span className="text-[13px] text-black/80">{type}</span>
              </label>;
      })}
        </div>}
    </div>;
};
const getNewActiveFileTypes = (fileType: string, prev: Record<string, boolean>) => {
  const newActiveFileTypes = {
    ...prev
  };
  newActiveFileTypes[fileType] = !prev[fileType];
  return newActiveFileTypes;
};
export const isFilterTooBig = (filterSizeLimit: number, scopesAndFiles: ScopesObject, caseId: string) => {
  if (filterSizeLimit === 0) {
    return false;
  }
  const filter = buildScopeFilter(scopesAndFiles.scopes, scopesAndFiles.files, scopesAndFiles.excludedFiles, caseId, 'case_id');
  const filterString = JSON.stringify(filter);
  const filterSize = filterString ? filterString.length : 0;
  if (filterSize > filterSizeLimit) {
    return true;
  }
  return false;
};
const sortScopes = (scopes: string[]) => {
  // collator would sort faster, but we would still have to sort by slash count again
  // const alphaSort = scopes.sort((a, b) => scopeCollator.compare(a, b));
  const slashSort = scopes.sort((a, b) => {
    const aSlashCount = (a.match(/\//g) || []).length;
    const bSlashCount = (b.match(/\//g) || []).length;
    if (aSlashCount !== bSlashCount) {
      return aSlashCount - bSlashCount;
    }
    return a.toLowerCase().localeCompare(b.toLowerCase());
  });
  return slashSort;
};
export const QuestionBar: React.FC<QuestionBarProps> = ({
  caseId,
  onSubmit,
  userId,
  sessionId,
  onClearThread,
  isLoading = false,
  isResetting,
  setQuery,
  query,
  textareaRef,
  hasChatHistory,
  isExpanded,
  setIsExpanded,
  collapseSources,
  scopesAndFiles,
  setScopesAndFiles,
  showFolders,
  setShowFolders,
  allFileTypes,
  setAllFileTypes,
  activeFileTypes,
  setActiveFileTypes,
  evidenceFilter,
  addSourceToScope,
  addToTimeline,
  featureFlagsProp,
  triggerPromptLibrary
}) => {
  const {
    width
  } = useWindowSize();
  const isSmallScreen = width !== undefined && width < 450;
  const [isAlertOpen, setIsAlertOpen] = useState(isResetting);
  const selectedItemsRef = useRef<HTMLDivElement>(null);
  const featuresContext = useContext(FeaturesContext);
  const statsContext = useContext(StatsContext);
  const fullScreenContext = useContext(FullScreenContext);
  const walkthruContext = useContext(WalkthruContext);
  const mediaSourceShare = useMediaSourceShare();

  // Safely extract values with fallbacks
  const featureFlags = featuresContext?.featureFlags || {};
  const handleDownloadWithIntercomFlag = useCallback((sourceObject: SourceObject | Source) => handleDownload(sourceObject, !!featureFlags?.useIntercom), [featureFlags?.useIntercom]);
  const contentCategoriesEnabled = !!featureFlags?.contentCategories;
  const {
    isFullscreen,
    setIsFullscreen,
    fullscreenMediaItem,
    setFullscreenMediaItem,
    linkedMediaItem,
    setLinkedMediaItem
  } = fullScreenContext || {};
  const {
    walkthrus,
    setWalkthru
  } = walkthruContext || {};
  const [loadingEvidence, setLoadingEvidence] = useState(false);
  const [availableEvidence, setAvailableEvidence] = useState<EvidenceObject[]>([]);
  const [allEvidence, setAllEvidence] = useState<EvidenceObject[]>([]);
  const [selectedEvidence, setSelectedEvidence] = useState<string[]>([]);
  const [excludedEvidence, setExcludedEvidence] = useState<string[]>([]);
  const [filesInputValue, setEvidenceInputValue] = useState<string>('');
  const [selectedEvidenceTypes, setSelectedEvidenceTypes] = useState<string[]>([]);
  const [selectedContentCategories, setSelectedContentCategories] = useState<string[]>([]);
  const [loadingScopes, setLoadingScopes] = useState(false);
  const [availableScopes, setAvailableScopes] = useState<string[]>([]);
  const [allScopes, setAllScopes] = useState<string[]>([]);
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [excludedScopes, setExcludedScopes] = useState<string[]>([]);
  const [scopeInputValue, setScopeInputValue] = useState<string>('');
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [copiedSource, setCopiedSource] = useState<number>(-1);
  const [addedSource, setAddedSource] = useState<number>(-1);
  const [localIsLoading, setLocalIsLoading] = useState(false);
  const [localQuery, setLocalQuery] = useState<string>(query);
  const [hasText, setHasText] = useState(false);
  const [isFullyExpanded, setIsFullyExpanded] = useState(false);
  const [isPromptLibraryOpen, setIsPromptLibraryOpen] = useState(false);
  const [previewPopoverOpen, setPreviewPopoverOpen] = useState<number>(-1);
  const [hoveredIconIndex, setHoveredIconIndex] = useState<number | null>(null);
  const [filterIsTooBig, setFilterIsTooBig] = useState(false);
  const [strandsModeQuerySent, setStrandsModeQuerySent] = useState(false);
  const [provenanceStatuses, setProvenanceStatuses] = useState<Record<string, any>>({});
  const [filesSearchTerm] = useDebounce(filesInputValue, 500);
  const [scopeSearchTerm] = useDebounce(scopeInputValue, 500);
  const formRef = useRef<HTMLFormElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const evidenceLockerRef = useRef<HTMLDivElement>(null);
  const evidenceLockerContentRef = useRef<HTMLDivElement>(null);
  const filterBarRowRef = useRef<HTMLDivElement>(null);
  const promptLibraryRef = useRef<HTMLDivElement>(null);
  const filesQueryRef = useRef<string>('');
  const evidenceAbortRef = useRef<AbortController | null>(null);
  const addSourcesButtonRef = useRef<HTMLDivElement>(null);
  const assistantQueryButtonRef = useRef<HTMLButtonElement>(null);
  const previewPopoverRef = useRef<HTMLDivElement>(null);
  const popoverStateRef = useRef<number | null>();
  const caseIdRef = useRef<string | null>(null);
  const iconMap = useMemo(() => getFileTypeIconsControllersIconsMap(), []);
  const {
    idToken,
    accessToken,
    tenantName
  } = useContext(AuthContext);

  // Handle external trigger of prompt library
  useEffect(() => {
    if (triggerPromptLibrary) {
      setIsPromptLibraryOpen(true);
    }
  }, [triggerPromptLibrary]);
  const handleSetPreviewPopoverOpen = (index: number) => {
    setIsExpanded(true);
    setIsFilterVisible(true);
    setPreviewPopoverOpen(index);
    const sourceItem = availableEvidence[index];
    if (!!sourceItem) {
      const mediaObject = {
        mediaType: sourceItem.mediaType,
        mediaUrl: sourceItem.url
      } as unknown as MediaObject;
      setFullscreenMediaItem(mediaObject);
    } else {
      if (!linkedMediaItem) {
        setFullscreenMediaItem(null);
      }
    }
  };
  useEffect(() => {
    if (isFullscreen || previewPopoverOpen !== null && previewPopoverOpen !== -1) {
      popoverStateRef.current = previewPopoverOpen;
    } else {
      if (popoverStateRef.current !== null && popoverStateRef.current !== -1 && popoverStateRef.current !== undefined) {
        setPreviewPopoverOpen(popoverStateRef.current !== undefined ? popoverStateRef.current : -1);
        setIsExpanded(true);
        setIsFilterVisible(true);
      }
      popoverStateRef.current = null;
    }
  }, [isFullscreen]);
  useEffect(() => {
    if (!isResetting) {
      setIsAlertOpen(false);
    }
  }, [isResetting]);
  useEffect(() => {
    if (!isExpanded || !isFilterVisible) {
      setPreviewPopoverOpen(-1);
    }
  }, [isFilterVisible, isExpanded]);
  useEffect(() => {
    setLocalIsLoading(isLoading);
  }, [isLoading]);
  useEffect(() => {
    if (evidenceFilter && evidenceFilter.length > 0 && evidenceFilter.trim() !== '') {
      setEvidenceInputValue(evidenceFilter);
      setScopeInputValue(evidenceFilter);
      setIsExpanded(true);
      setIsFilterVisible(true);
    }
  }, [evidenceFilter]);
  useEffect(() => {
    if (isExpanded) {
      // Set a timeout to match the animation duration (0.7s from the motion.div)
      const timer = setTimeout(() => {
        setIsFullyExpanded(true);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setIsFullyExpanded(false);
    }
  }, [isExpanded]);
  const handleIncludeAllChange = useCallback((_filterKey: string, _checked: boolean) => {
    // Kept for backward compatibility; scope selection now handled via ScopeDropdown
  }, []);
  const handleSubmit = async (e: React.FormEvent, useDeepDive: boolean = false) => {
    if (featureFlags?.filterSizeLimit && isFilterTooBig(featureFlags?.filterSizeLimit as unknown as number, scopesAndFiles, caseId)) {
      return;
    }
    e.preventDefault();
    if (!textareaRef?.current?.value || textareaRef?.current?.value?.trim() === '' || localIsLoading) return;
    setQuery(textareaRef?.current?.value || '');
    if (useDeepDive) {
      setStrandsModeQuerySent(true);
      setTimeout(() => {
        setStrandsModeQuerySent(false);
      }, 1000);
    }
    try {
      await onSubmit(textareaRef.current?.value || '', {
        files: scopesAndFiles?.files ?? [],
        scopes: scopesAndFiles?.scopes ?? [],
        excludedFiles: scopesAndFiles?.excludedFiles ?? [],
        excludedScopes: scopesAndFiles?.excludedScopes ?? [],
        ...(featureFlags?.contentCategories && {
          contentCategories: selectedContentCategories
        })
      }, userId, sessionId, true, 0, useDeepDive);
      setQuery('');
      collapseSources();
    } catch (error) {
      console.error('Error submitting query:', error);
      // Handle error (e.g., show an error message to the user)
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      if (featureFlags?.filterSizeLimit && isFilterTooBig(featureFlags?.filterSizeLimit as unknown as number, scopesAndFiles, caseId)) {
        setFilterIsTooBig(true);
        return;
      }
      setFilterIsTooBig(false);
      e.preventDefault();

      // Create a synthetic button click event
      const button = assistantQueryButtonRef.current;
      if (button) {
        const syntheticEvent = {
          target: button,
          currentTarget: button,
          preventDefault: () => {},
          type: 'click'
        } as unknown as React.MouseEvent<HTMLButtonElement>;
        const statsContextLocal = {
          userInfo: {
            userId: statsContext?.userInfo?.userId || userId || '',
            sessionId: statsContext?.userInfo?.sessionId || sessionId || '',
            caseId: statsContext?.userInfo?.caseId || caseId || '',
            query: textareaRef.current?.value || ''
          }
        };
        statsContext.recordStat('AssistantQueryButtonClicked', syntheticEvent, statsContextLocal, !!featureFlags?.useIntercom);
      }
      handleSubmit(e as unknown as React.FormEvent, false);
    }
  };
  const handleAddScopesAndFiles = ({
    scopesToAdd,
    filesToAdd,
    excludedScopesToAdd,
    excludedFilesToAdd
  }: {
    scopesToAdd: string[];
    filesToAdd: string[];
    excludedScopesToAdd: string[];
    excludedFilesToAdd: string[];
  }) => {
    const newScopes = new Set([...(scopesAndFiles?.scopes || []), ...scopesToAdd]);
    const newFiles = new Set([...(scopesAndFiles?.files || []), ...filesToAdd]);
    const newExcludedScopes = new Set([...(scopesAndFiles?.excludedScopes || []), ...excludedScopesToAdd]);
    const newExcludedFiles = new Set([...(scopesAndFiles?.excludedFiles || []), ...excludedFilesToAdd]);
    const newScopesAndFiles = {
      scopes: Array.from(newScopes),
      files: Array.from(newFiles),
      excludedScopes: Array.from(newExcludedScopes),
      excludedFiles: Array.from(newExcludedFiles)
    };
    setScopesAndFiles(newScopesAndFiles);
    setSelectedScopes([]);
    setSelectedEvidence([]);
    setExcludedScopes([]);
    setExcludedEvidence([]);
  };
  const changeHeight = (elem: HTMLTextAreaElement) => {
    if (elem.value.length < 70) {
      elem.style.height = '20px';
    } else {
      elem.style.height = elem.scrollHeight + 'px';
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    changeHeight(e.target);
    setHasText(!!e.target.value.trim());
    setLocalQuery(e.target.value);
  };
  const fetchProvenanceStatus = async (s3Uri: string) => {
    try {
      const body = {
        endpointPath: '/statuses/file-status',
        s3Uris: [s3Uri]
      };
      const response = await FetchWithVersionCheck('/api/protected/provenance-service-proxy/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      let newProvenanceStatuses: {
        [key: string]: {
          status: string;
          iconStatus: string;
        };
      } = {
        ...provenanceStatuses
      };
      for (const key in data) {
        const newStatus = data[key];
        if (newStatus.status) {
          if (isStatus(newStatus.status.toLowerCase(), 'INTERMEDIARY')) {
            newStatus.iconStatus = 'INTERMEDIARY';
          } else if (isStatus(newStatus.status.toLowerCase(), 'ERROR')) {
            newStatus.iconStatus = 'ERROR';
          } else if (isStatus(newStatus.status.toLowerCase(), 'SUCCESS')) {
            newStatus.iconStatus = 'SUCCESS';
          } else {
            newStatus.iconStatus = 'UNKNOWN';
          }
        }
        newProvenanceStatuses[s3Uri] = {
          status: newStatus.status.toLowerCase(),
          iconStatus: newStatus.iconStatus
        };
      }
      setProvenanceStatuses(newProvenanceStatuses);
    } catch (error) {
      setProvenanceStatuses({
        ...provenanceStatuses,
        [s3Uri]: {
          status: 'NOT AVAILABLE',
          iconStatus: 'NOT AVAILABLE'
        }
      });
    }
  };
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.value = localQuery;
      changeHeight(textareaRef.current);
    }
  }, [localQuery]);
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.value = query;
      changeHeight(textareaRef.current);
      setHasText(!!query.trim());
    }
  }, [query]);
  useEffect(() => {
    if (!accessToken || !caseId || caseId.trim() === '') {
      return;
    }
    if (caseId !== caseIdRef.current) {
      if (caseIdRef.current !== null) {
        // Reset the question bar entirely.
        setLocalQuery('');
        setHasText(false);
        setQuery('');
        if (textareaRef.current) {
          textareaRef.current.value = '';
          changeHeight(textareaRef.current);
        }
        setScopeInputValue('');
        setEvidenceInputValue('');
        setScopesAndFiles({
          scopes: [],
          files: [],
          excludedScopes: [],
          excludedFiles: []
        });
      }
      caseIdRef.current = caseId;
      const fetchScopeOptions = async () => {
        try {
          setLoadingScopes(true);
          const scopeParam = encodeURIComponent(caseId || '*');
          const response = await FetchWithVersionCheck(`/api/protected/cases/${scopeParam}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({
              primary_only: false
            })
          });
          const data = await response.json();
          if (!response.ok) {
            throw new Error(`Failed to fetch cases: ${response.statusText}`);
          }
          if (!data.options) {
            throw new Error('No cases available');
          }
          // Sort the options alphabetically
          const sortedOptions = sortScopes(data.options.filter((option: string) => option !== caseId));
          setAvailableScopes(sortedOptions.slice(0, 250));
          setAllScopes(sortedOptions);
        } catch (error) {
          const errorMessage = `Error fetching case scope options for ${caseId}: ${error}`;
          CaptureError(errorMessage, error as Error, !!featureFlags?.useIntercom);
          console.warn('Error fetching scope options:', error);
          setAvailableScopes([]);
          setAllScopes([]);
        } finally {
          setLoadingScopes(false);
        }
      };
      fetchScopeOptions();
    }
  }, [caseId, accessToken]);
  useEffect(() => {
    if (featureFlags?.filterSizeLimit && isFilterTooBig(featureFlags?.filterSizeLimit as unknown as number, scopesAndFiles, caseId)) {
      setFilterIsTooBig(true);
    } else {
      setFilterIsTooBig(false);
    }
  }, [scopesAndFiles, caseId, featureFlags?.filterSizeLimit]);
  useEffect(() => {
    setLoadingScopes(true);
    setAvailableScopes([]);
    if (scopeSearchTerm.trim().length > 2) {
      let scopeSearchTermToUse = scopeSearchTerm;
      if (scopeSearchTermToUse.startsWith('"') && scopeSearchTermToUse.endsWith('"')) {
        scopeSearchTermToUse = scopeSearchTermToUse.slice(1, -1);
      } else {
        scopeSearchTermToUse = scopeSearchTermToUse.trim();
      }
      setAvailableScopes(allScopes.filter((option: string) => {
        return option.toLowerCase().includes(scopeSearchTermToUse.toLowerCase());
      }));
    } else {
      setAvailableScopes(allScopes);
    }
    setLoadingScopes(false);
  }, [scopeSearchTerm]);
  useEffect(() => {
    setAvailableEvidence([]);
    const fileTypesToSearch = Object.keys(activeFileTypes).filter(key => activeFileTypes[key] === true);
    if (showFolders && fileTypesToSearch.length < 1 && !allFileTypes) {
      return;
    }
    const fileTypesToSearchString = fileTypesToSearch.join(',');
    if (!caseId || caseId.trim() === '') {
      return;
    }
    setSelectedEvidence([]);
    setSelectedScopes([]);
    filesQueryRef.current = `${caseId}-${filesSearchTerm.trim()}-${fileTypesToSearchString}`;
    let filesSearchTermToUse = filesSearchTerm;
    if (filesSearchTermToUse.startsWith('"') && filesSearchTermToUse.endsWith('"')) {
      filesSearchTermToUse = filesSearchTermToUse.slice(1, -1);
    } else {
      filesSearchTermToUse = filesSearchTermToUse.trim();
    }
    evidenceAbortRef.current?.abort();
    evidenceAbortRef.current = new AbortController();
    const controller = evidenceAbortRef.current;
    const fetchEvidence = async () => {
      try {
        const evidenceUrl = !!featureFlags?.provenanceService ? '/api/protected/provenance-service-proxy' : '/api/protected/evidence';
        setLoadingEvidence(true);
        const body = {
          caseId: caseId,
          search: filesSearchTermToUse,
          fileTypes: fileTypesToSearch
        };
        const filesResponse = await FetchWithVersionCheck(evidenceUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'x-tai-id-token': idToken || ''
          },
          body: JSON.stringify(body),
          // NOTE -- Jake Mason | (2026-02-12):
          // We'll abort either if a new search is fired off or if we hit the limit of 90 seconds. This
          // 90 second limit was chosen to accommodate customers who potentially have a massive number of
          // files to match against. Possibly something to consider improving in the future.
          signal: AbortSignal.any([controller.signal, AbortSignal.timeout(90000)])
        });
        // FetchWithVersionCheck returns a synthetic 500 on abort; check before parsing to avoid
        // misclassifying canceled requests as fetch failures (and clearing evidence).
        if (controller.signal.aborted) return;
        const filesResponseJson = await filesResponse.json();
        let fileSources: EvidenceObject[] = [];
        if (!!featureFlags?.provenanceService && Object.hasOwn(filesResponseJson, "statuses")) {
          if (filesResponseJson["statuses"].length === 0) {
            setAvailableEvidence([]);
            setAllEvidence([]);
            setLoadingEvidence(false);
            return;
          }
          fileSources = filesResponseJson["statuses"].map((source: any) => {
            // Try multiple possible field names for status
            const statusValue = source.provenance_status || source.verification_status || source.status || source.state || source.condition || 'unknown';
            return {
              ...source,
              url: source.source_s3_path,
              mediaType: source.file_type,
              status: statusValue
            };
          });
        } else {
          // When not using provenance service, ensure sources have status field
          fileSources = filesResponseJson.sources?.map((source: any) => ({
            ...source,
            status: source.status || 'unknown'
          })) || [];
        }
        setAvailableEvidence(fileSources);
        setAllEvidence(fileSources);
        setLoadingEvidence(false);
      } catch (error) {
        // AbortError can occur if fetch throws before FetchWithVersionCheck catches (browser-dependent).
        // If we reach catch and signal is aborted, treat as canceled rather than a real failure.
        if (error instanceof Error && error.name === 'AbortError' || controller.signal.aborted) return;
        console.warn('Error fetching evidence:', error);
        setAvailableEvidence([]);
        setAllEvidence([]);
      } finally {
        // Clear loading when: (1) we're still the current request, or (2) ref is null
        // (cleanup ran, no replacement). Skip clearing only when superseded by a newer
        // request (ref holds a different controller).
        if (controller === evidenceAbortRef.current || evidenceAbortRef.current === null) {
          setLoadingEvidence(false);
        }
      }
    };
    if (!!featureFlags) {
      fetchEvidence();
    }
    return () => {
      evidenceAbortRef.current?.abort();
      evidenceAbortRef.current = null;
    };
  }, [caseId, filesSearchTerm, activeFileTypes, showFolders, allFileTypes, featureFlags]);
  useEffect(() => {
    let newEvidenceSources: EvidenceObject[] = [];
    availableEvidence?.forEach((files: EvidenceObject) => {
      newEvidenceSources.push(files);
    });
    setAllEvidence(newEvidenceSources);
  }, [availableEvidence]);
  const hotkeyHandlers = {
    'a': (e: KeyboardEvent) => {
      const activeElement = document.activeElement;

      // Only proceed if the active element is the body (no focused elements)
      if (activeElement === document.body) {
        e.preventDefault();
        textareaRef.current?.focus();
        handleExpandClick(e as unknown as React.MouseEvent<HTMLButtonElement, MouseEvent>); // Expand the assistant search field
      }
    }
  };
  useHotkeys(Object.keys(hotkeyHandlers).join(', '), (e, handler) => {
    if (Array.isArray(handler.keys)) {
      const key = handler.keys[0];
      if (key in hotkeyHandlers) {
        hotkeyHandlers[key as keyof typeof hotkeyHandlers]?.(e);
      }
    }
  }, {
    enableOnFormTags: true,
    preventDefault: false,
    enabled: true
  });
  const handlePromptSelect = (selectedQuery: string) => {
    // Focus the textarea
    textareaRef.current?.focus();

    // Update the textarea value
    if (textareaRef.current) {
      textareaRef.current.value = selectedQuery;
    }

    // Update the query state
    setLocalQuery(selectedQuery);

    // Update the hasText state
    setHasText(!!selectedQuery.trim());
  };
  const handleExpandClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!isExpanded) {
      setIsExpanded(true);
    }
  };
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.value = query;
    }
    const handleDocumentClick = (event: MouseEvent) => {
      if (isExpanded && !formRef.current?.contains(event.target as Node) && !previewPopoverRef.current?.contains(event.target as Node) && !document.getElementById('fullScreenMediaDisplayOuter')?.contains(event.target as Node) && !promptLibraryRef.current?.contains(event.target as Node)) {
        const textareaValue = textareaRef.current?.value?.trim() || '';
        if (!textareaValue) {
          setIsExpanded(false);
        }
      }
    };
    document.addEventListener('mousedown', handleDocumentClick);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [isSmallScreen, formRef, query, setIsExpanded, textareaRef, isExpanded]);
  useEffect(() => {
    if (!isExpanded) {
      if (textareaRef.current) {
        textareaRef.current.style.height = '0px';
      }
      if (isFilterVisible) {
        setIsFilterVisible(false);
      }
      // TODO: the code below was causing mediaDisplay to be empty when feedback was opened
      // if (fullscreenMediaItem !== null && !linkedMediaItem) {
      //   setFullscreenMediaItem(null);
      // }
      if (previewPopoverOpen !== -1) {
        setPreviewPopoverOpen(-1);
      }
    }
  }, [isExpanded]);
  const addToSelectedSources = (sourceUrl: string) => {
    if (!selectedEvidence.includes(sourceUrl)) {
      setSelectedEvidence([...selectedEvidence, sourceUrl]);
      setExcludedEvidence(excludedEvidence.filter(source => source !== sourceUrl));
      setIsExcludeAllActive(false);
      setIsIncludeAllActive(false);
    } else {
      setSelectedEvidence(selectedEvidence.filter(source => source !== sourceUrl));
      setIsExcludeAllActive(false);
      setIsIncludeAllActive(false);
    }
  };
  const addToSelectedScopes = (scopeIn: string) => {
    if (!selectedScopes.includes(scopeIn)) {
      setSelectedScopes([...selectedScopes, scopeIn]);
      setExcludedScopes([]);
      setIsExcludeAllActive(false);
      setIsIncludeAllActive(false);
    } else {
      setSelectedScopes(selectedScopes.filter(scope => scope !== scopeIn));
      setIsExcludeAllActive(false);
      setIsIncludeAllActive(false);
    }
  };
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [isExcludeAllActive, setIsExcludeAllActive] = useState(false);
  const [isIncludeAllActive, setIsIncludeAllActive] = useState(false);
  const getIconStyle = (isActive: boolean) => {
    return `cursor-pointer hover:text-black ${isActive ? 'text-black' : 'text-brand-blue-light'}`;
  };
  const getItemStyle = (isExcluded: boolean) => {
    return isExcluded ? 'border-red-500 border-solid bg-red-100' : 'border-[#2d8dfe] border-solid bg-[#99C4F6]/30';
  };
  const removeScopeOrFile = (fileOrScope: string) => {
    let newScopesAndFiles = {
      ...scopesAndFiles
    };
    if (scopesAndFiles?.files.includes(fileOrScope)) {
      newScopesAndFiles.files = scopesAndFiles.files.filter(f => f !== fileOrScope);
      setScopesAndFiles(newScopesAndFiles);
    } else if (scopesAndFiles?.scopes.includes(fileOrScope)) {
      newScopesAndFiles.scopes = scopesAndFiles.scopes.filter(s => s !== fileOrScope);
      setScopesAndFiles(newScopesAndFiles);
    } else if (scopesAndFiles?.excludedFiles.includes(fileOrScope)) {
      newScopesAndFiles.excludedFiles = scopesAndFiles.excludedFiles.filter(f => f !== fileOrScope);
      setScopesAndFiles(newScopesAndFiles);
    } else if (scopesAndFiles?.excludedScopes.includes(fileOrScope)) {
      newScopesAndFiles.excludedScopes = scopesAndFiles.excludedScopes.filter(s => s !== fileOrScope);
      setScopesAndFiles(newScopesAndFiles);
    }
  };
  const getHoverButtonStyle = (fileOrScope: string) => {
    const isScope = scopesAndFiles?.scopes?.includes(fileOrScope) || scopesAndFiles?.excludedScopes?.includes(fileOrScope);
    if (isScope) {
      return <></>;
    }
    const isExcluded = scopesAndFiles?.excludedFiles?.includes(fileOrScope) || scopesAndFiles?.excludedScopes?.includes(fileOrScope);
    const handleClick = (e: React.SyntheticEvent) => {
      e.stopPropagation();
      let newScopesAndFiles = scopesAndFiles ? {
        ...scopesAndFiles
      } : {
        scopes: [],
        files: [],
        excludedScopes: [],
        excludedFiles: []
      };
      if (scopesAndFiles?.files.includes(fileOrScope)) {
        // remove from files and add to excludedFiles
        newScopesAndFiles.excludedFiles = [...(scopesAndFiles.excludedFiles || []), fileOrScope];
        newScopesAndFiles.files = scopesAndFiles.files.filter(f => f !== fileOrScope);
      } else if (scopesAndFiles?.scopes.includes(fileOrScope)) {
        // remove from scopes and add to excludedScopes
        newScopesAndFiles.excludedScopes = [...(scopesAndFiles.excludedScopes || []), fileOrScope];
        newScopesAndFiles.scopes = scopesAndFiles.scopes.filter(s => s !== fileOrScope);
      } else if (scopesAndFiles?.excludedFiles.includes(fileOrScope)) {
        // remove from excludedFiles and add to files
        newScopesAndFiles.files = [...(scopesAndFiles.files || []), fileOrScope];
        newScopesAndFiles.excludedFiles = scopesAndFiles.excludedFiles.filter(f => f !== fileOrScope);
      } else if (scopesAndFiles?.excludedScopes.includes(fileOrScope)) {
        // remove from excludedScopes and add to scopes
        newScopesAndFiles.scopes = [...(scopesAndFiles.scopes || []), fileOrScope];
        newScopesAndFiles.excludedScopes = scopesAndFiles.excludedScopes.filter(s => s !== fileOrScope);
      }
      setScopesAndFiles(newScopesAndFiles);
    };
    return isExcluded ? <div className="absolute right-[1px] top-[1px]" data-sentry-component="getHoverButtonStyle" data-sentry-source-file="question-bar.tsx">
          <div className="flex flex-row items-center justify-center gap-1 bg-black rounded-full p-1">
            <button role="button" tabIndex={0} aria-label="Include this item" className="cursor-pointer text-white/40 hover:text-white/80 focus:outline focus:outline-2 focus:outline-white rounded" onClick={e => {
          e.stopPropagation();
          handleClick(e);
        }} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handleClick(e);
          }
        }}>
              <PlusCircleIcon size={20} weight="regular" data-sentry-element="PlusCircleIcon" data-sentry-source-file="question-bar.tsx" />
            </button>
          </div>
        </div> : <div className="absolute right-[1px] top-[1px]" data-sentry-component="getHoverButtonStyle" data-sentry-source-file="question-bar.tsx">
          <div className="flex flex-row items-center justify-center gap-1 bg-black rounded-full p-1">
            <button role="button" tabIndex={0} aria-label="Exclude this item" className="cursor-pointer text-white/40 hover:text-white/80 focus:outline focus:outline-2 focus:outline-white rounded" onClick={e => {
          e.stopPropagation();
          handleClick(e);
        }} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handleClick(e);
          }
        }}>
              <MinusCircleIcon size={20} weight="regular" data-sentry-element="MinusCircleIcon" data-sentry-source-file="question-bar.tsx" />
            </button>
          </div>
        </div>;
  };
  const toggleFileType = (fileType: string) => {
    const updatedActiveFileTypes = getNewActiveFileTypes(fileType, activeFileTypes);
    const isNowActive = updatedActiveFileTypes[fileType] === true;
    if (Object.values(updatedActiveFileTypes).some(value => value === true)) {
      setAllFileTypes(false);
    }
    setActiveFileTypes(updatedActiveFileTypes);
  };
  const removeContentCategory = (fileType: string) => {
    setSelectedContentCategories(selectedContentCategories.filter(ft => ft !== fileType));
    const updatedActiveFileTypes = {
      ...activeFileTypes
    };
    delete updatedActiveFileTypes[fileType];
    setActiveFileTypes(updatedActiveFileTypes);
  };
  const toggleShowFolders = () => {
    setShowFolders(!showFolders);
  };
  const toggleAllFileTypes = () => {
    const newAllFileTypes = !allFileTypes;
    if (!allFileTypes) {
      setActiveFileTypes({});
    }
    setAllFileTypes(newAllFileTypes);
  };
  const handleSelectAll = () => {
    setExcludedEvidence([]);
    setExcludedScopes([]);
    if (!showFolders || allFileTypes || Object.keys(activeFileTypes).filter(key => activeFileTypes[key] === true).length > 0) {
      setSelectedEvidence(availableEvidence?.map(source => (source as any).url) || []);
    }
    if (showFolders || !allFileTypes && Object.keys(activeFileTypes).filter(key => activeFileTypes[key] === true).length < 1) {
      setSelectedScopes(availableScopes || []);
    }
    setIsIncludeAllActive(true);
    setIsExcludeAllActive(false);
  };
  const handleCopySourceForTimeline = async (source: Source, index: number) => {
    try {
      const pathToCopy = extractPathAfterBulk(source.sourceFilePath || source.truncatedFilePath || '').truncatedFilePath;
      source.sourceContext = {
        ...source.sourceContext,
        transcriptUrl: source.location,
        transcriptPosition: 'below',
        showTranscriptMarkers: true,
        showTranscript: true,
        pageNumber: source.page || 1,
        startTime: source.sourceContext?.startTime || 0,
        previewUrl: source.previewFilePath || source.derived_object_url
      };
      const sourceToCopy = {
        source,
        caseId: caseId
      };
      if (pathToCopy) {
        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(pathToCopy);
          } else {
            // Fallback for non-secure contexts
            const textArea = document.createElement('textarea');
            textArea.value = pathToCopy;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            document.execCommand('copy');
            textArea.remove();
          }
          sessionStorage.setItem('source', JSON.stringify(sourceToCopy));
          setCopiedSource(index);
        } catch (clipboardError) {
          console.warn('Clipboard operation failed:', clipboardError);
          // Still set the copied source even if clipboard fails
          sessionStorage.setItem('source', JSON.stringify(sourceToCopy));
          setCopiedSource(index);
        }
      }
    } catch (error) {
      console.error('Failed to copy source path:', error);
    }
  };
  const handleFocus = () => {
    // do nothing - but maybe stop polling
  };
  const handleBlur = () => {
    // do nothing - but maybe start polling
  };
  useEffect(() => {
    if (copiedSource !== -1) {
      setTimeout(() => {
        setCopiedSource(-1);
      }, 1500);
    }
  }, [copiedSource]);
  useEffect(() => {
    if (addedSource !== -1) {
      setTimeout(() => {
        setAddedSource(-1);
      }, 1500);
    }
  }, [addedSource]);
  useEffect(() => {
    if (previewPopoverOpen !== -1) {
      const handleClickOutside = (event: MouseEvent) => {
        if (previewPopoverRef.current && !previewPopoverRef.current.contains(event.target as Node)) {
          handleSetPreviewPopoverOpen(-1);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [previewPopoverOpen]);
  const totalResults = Math.min((!showFolders || allFileTypes || Object.keys(activeFileTypes).filter(key => activeFileTypes[key] === true).length > 0 ? Math.min(availableEvidence?.length || 0, 250) : 0) + (!allFileTypes && Object.keys(activeFileTypes).filter(key => activeFileTypes[key] === true).length < 1 || showFolders ? Math.min(availableScopes?.length || 0, 250) : 0), 500);
  const isSourceCardPopoverOpen = previewPopoverOpen !== -1 && isFilterVisible && !isFullscreen;
  const hasResults = availableEvidence?.length > 0 && (!showFolders || allFileTypes || Object.keys(activeFileTypes).filter(key => activeFileTypes[key] === true).length > 0) || availableScopes?.length > 0 && (!allFileTypes || showFolders);
  const renderResultsArea = (wrapperClassName: string) => <>
      {hasResults ? <div className={wrapperClassName}>
          <div className="w-full">
            <div className="w-full flex items-center justify-between mb-2 border-b-3 border-brand-blue-light/50 border-double">
              {loadingEvidence || loadingScopes ? <div className="flex items-center justify-center h-full" aria-live="polite" aria-busy="true" role="status">
                  <div className="animate-pulse font-bold p-1 pt-0">Loading {loadingEvidence ? `evidence${loadingScopes ? ' and scopes' : ''}` : 'scopes'}...</div>
                </div> : <span className="text-sm">
                  Showing the top {totalResults} result(s)
                </span>}
              <div className={`flex gap-1 items-center ${featureFlags?.provenanceService ? 'pr-[64px]' : 'pr-[42px]'}`}>
                <button aria-label="Clear all selections" className={`${selectedEvidence.length > 0 || selectedScopes.length > 0 || excludedEvidence.length > 0 || excludedScopes.length > 0 ? getIconStyle(true) : 'text-brand-blue-light cursor-default hidden'}`} onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              e.stopPropagation();
              if (selectedEvidence.length > 0 || selectedScopes.length > 0 || excludedEvidence.length > 0 || excludedScopes.length > 0) {
                setSelectedEvidence([]);
                setSelectedScopes([]);
                setExcludedEvidence([]);
                setExcludedScopes([]);
                setIsExcludeAllActive(false);
                setIsIncludeAllActive(false);
              }
            }}>
                  Clear All
                </button>
                <button aria-label="Select all items" className={`${getIconStyle(isIncludeAllActive)} ${isIncludeAllActive ? 'hidden' : ''}`} onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              e.stopPropagation();
              handleSelectAll();
            }}>
                  Select All
                </button>
              </div>
            </div>
            <div className="max-h-[160px] overflow-x-hidden overflow-y-auto custom-scrollbar3 pr-2 w-full">
              {isFilterVisible && (!allFileTypes && Object.keys(activeFileTypes).filter(key => activeFileTypes[key] === true).length < 1 || showFolders) && availableScopes && availableScopes.length > 0 && availableScopes.filter(scope => !scopesAndFiles?.scopes?.includes(scope) && !scopesAndFiles?.excludedScopes?.includes(scope)).slice(0, 250).map((scope, index) => {
            const folderName = removeMD5Hash(stripUnwantedLeadingPathSegments(folderPathForEvidenceLockerDisplay(scope, caseId)));
            return <div key={`${scope}-${index}`} className="box-border flex flex-row justify-between items-center gap-1 m-0 mb-2 w-full">
                      <div className={`box-border w-full flex flex-row justify-left items-center gap-1 m-0 rounded-md border-1
                        ${!featureFlags?.provenanceService ? 'max-w-[calc(100%-50px)]' : ''}
                        ${featureFlags?.provenanceService ? 'max-w-[calc(100%-80px)]' : ''}
                        ${selectedScopes.includes(scope) || excludedScopes.includes(scope) ? getItemStyle(excludedScopes.includes(scope)) : 'border-brand-blue-light/20 border-dashed hover:border-brand-blue-light/50 hover:border-solid hover:bg-[#99C4F6]/10'} cursor-pointer p-0.5`} onClick={e => {
                e.stopPropagation();
                addToSelectedScopes(scope);
              }}>
                        <div className="flex items-center justify-center w-[20px] h-[20px]">
                          <FolderOpenIcon size={20} />
                        </div>
                        <div className="w-full justify-start cursor-pointer p-0.5 bg-transparent max-w-[calc(100%-10px)]">
                          <div className="text-sm truncate dir-rtl px-1 justify-start text-left w-full max-w-[calc(100%-25px)]">
                            <Tooltip className="w-[500px] max-w-[95vw]" content={folderName} classNames={{
                      content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
                      base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0 w-[500px] max-w-[95vw]'
                    }}>
                              <span>&lrm;{folderName}</span>
                            </Tooltip>
                          </div>
                        </div>
                      </div>
                      <div className={`flex flex-row items-center gap-1`}>
                        <Checkbox type="checkbox" classNames={{
                  base: 'min-w-[20px] max-w-[20px] min-h-[20px] max-h-[20px] mr-0',
                  wrapper: 'min-w-[20px] max-w-[20px] min-h-[20px] max-h-[20px] mr-0',
                  label: 'min-w-[20px] max-w-[20px] min-h-[20px] max-h-[20px] mr-0',
                  hiddenInput: 'min-w-[20px] max-w-[20px] min-h-[20px] max-h-[20px] mr-0'
                }} isSelected={selectedScopes.includes(scope)} onValueChange={() => {
                  addToSelectedScopes(scope);
                }} />
                        <div className="min-w-[20px] min-h-[20px]">
                          <EyeIcon size={20} weight="regular" className="text-white/10 h-[20px] w-[20px] min-w-[20px] min-h-[20px]" />
                        </div>
                        {!!featureFlags?.provenanceService && <div className="min-w-[20px] max-w-[20px] min-h-[20px]">
                            <FolderOpenIcon size={20} weight="regular" className="text-white/10 h-[20px] w-[20px] min-w-[20px] min-h-[20px]" />
                          </div>}
                      </div>
                    </div>;
          })}
              {isFilterVisible && (!showFolders || allFileTypes || Object.keys(activeFileTypes).filter(key => activeFileTypes[key] === true).length > 0) && availableEvidence && availableEvidence.length > 0 && availableEvidence.filter(source => !scopesAndFiles?.files?.includes((source as any).url) && !scopesAndFiles?.excludedFiles?.includes((source as any).url)).slice(0, 250).map((sourceObject, index) => {
            const icon: React.ElementType = iconMap[sourceObject.url.toLowerCase().split('.').pop() as keyof typeof iconMap] || iconMap['Other'];
            const mediaType = getMediaType(sourceObject as unknown as SourceObject, (sourceObject as unknown as SourceObject).sourceInfo);
            let useMetaViewer = false;
            let s3Key = '';
            if (mediaType === 'html') {
              s3Key = (sourceObject as any).url.startsWith('s3://') ? (sourceObject as any).url.split('/').slice(3).join('/') : (sourceObject as any).url;
              const lastPart = (s3Key.split('/').pop() || '').toLowerCase();
              if (META_FILE_REGEX.test(lastPart)) {
                useMetaViewer = true;
              }
            }
            const fileName = extractPathAfterBulk((sourceObject as any).url, true).truncatedFilePath;
            return <div key={`${(sourceObject as any).url}-${index}`} className="flex flex-row justify-between items-center gap-1 m-0 mb-2 w-full">
                      <div className={`box-border w-full flex flex-row justify-left items-center gap-1 m-0 rounded-md border-1
                        ${!featureFlags?.provenanceService ? 'max-w-[calc(100%-50px)]' : ''}
                        ${featureFlags?.provenanceService ? 'max-w-[calc(100%-80px)]' : ''}
                        ${(sourceObject as any).url && (selectedEvidence.includes((sourceObject as any).url) || excludedEvidence.includes((sourceObject as any).url)) ? getItemStyle(excludedEvidence.includes((sourceObject as any).url)) : 'border-brand-blue-light/20 border-dashed hover:border-brand-blue-light/50 hover:border-solid hover:bg-[#99C4F6]/10'} cursor-pointer p-0.5`} onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                addToSelectedSources((sourceObject as any).url);
              }} onMouseEnter={() => {
                if (featureFlags?.provenanceStatus && (sourceObject as any).url) {
                  fetchProvenanceStatus((sourceObject as any).url);
                }
              }}>
                        {featureFlags?.provenanceStatus && (sourceObject as any).url ? <Tooltip className="w-[260px] max-w-[95vw]" placement="top" delay={1000} offset={4} crossOffset={110} showArrow={true} classNames={{
                  content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
                  base: 'before:ml-[-110px] before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
                }} content={<>
                                {provenanceStatuses[(sourceObject as any).url]?.status ? <div className="flex flex-row items-center gap-1">
                                    <div className="bg-white rounded-full p-1 mr-1">
                                      <ProvenanceIcon status={provenanceStatuses[(sourceObject as any).url]?.status} size={24} className={`box-border h-[20px] w-[20px] min-w-[20px] min-h-[20px] max-h-[20px] max-w-[20px]
                                          ${getStatusColor(provenanceStatuses[(sourceObject as any).url]?.status)}
                                          ${isStatus(provenanceStatuses[(sourceObject as any).url]?.status, 'SUCCESS') ? 'border-2 border-green-300 rounded-md' : isStatus(provenanceStatuses[(sourceObject as any).url]?.status, 'ERROR') ? 'border-2 border-red-300 rounded-md' : isStatus(provenanceStatuses[(sourceObject as any).url]?.status, 'INTERMEDIARY') ? 'border-2 border-yellow-300 rounded-md' : 'border-2 border-gray-300 rounded-md'}
                                        `} aria-hidden="true" />
                                    </div>
                                    <div>
                                      Status: {(sourceObject as any).url ? provenanceStatuses[(sourceObject as any).url]?.status || 'UNKNOWN' : 'UNKNOWN'} - {getStatusDescription(provenanceStatuses[(sourceObject as any).url]?.status)}
                                    </div>
                                  </div> : <div>Fetching status...</div>}
                              </>}>
                            <div className="flex items-center justify-center min-w-[20px] max-w-[20px] h-[20px] hover:cursor-pointer">
                              <DynamicIcon icon={icon || FileIcon} size={20} weight="light" className="text-black/80 h-[20px] w-[20px] min-w-[20px] min-h-[20px]" />
                            </div>
                          </Tooltip> : <div className="flex items-center justify-center min-w-[20px] max-w-[20px] h-[20px] hover:cursor-pointer">
                            <DynamicIcon icon={icon || FileIcon} size={20} weight="light" className="text-black/80 h-[20px] w-[20px] min-w-[20px] min-h-[20px]" />
                          </div>}
                        <div className="w-full justify-start cursor-pointer p-0.5 bg-transparent max-w-[calc(100%-10px)]">
                          <div className="text-sm truncate dir-rtl px-1 justify-start text-left max-w-[calc(100%-25px)]">
                            <div>&lrm;{fileName}</div>
                          </div>
                        </div>
                      </div>
                      <div className={`flex flex-row items-center gap-1`}>
                        <Checkbox type="checkbox" classNames={{
                  base: 'min-w-[20px] max-w-[20px] min-h-[20px] max-h-[20px] mr-0',
                  wrapper: 'min-w-[20px] max-w-[20px] min-h-[20px] max-h-[20px] mr-0',
                  label: 'min-w-[20px] max-w-[20px] min-h-[20px] max-h-[20px] mr-0',
                  hiddenInput: 'min-w-[20px] max-w-[20px] min-h-[20px] max-h-[20px] mr-0'
                }} isSelected={selectedEvidence.includes((sourceObject as any).url)} onValueChange={() => {
                  addToSelectedSources((sourceObject as any).url);
                }} />
                        {!!featureFlags?.provenanceService && <Tooltip className="w-[300px] max-w-[95vw]" placement="left" delay={100} offset={4} showArrow={true} classNames={{
                  content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
                  base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
                }} content={`Status: ${(sourceObject as any).status || 'Unknown'} - ${getStatusDescription((sourceObject as any).status)}`}>
                          <div className={`flex flex-col items-center gap-1 rounded-md transition-colors duration-200
                              ${!!featureFlags?.provenanceService && (sourceObject as any).status ? 'bg-white/50 hover:bg-white/70' : ''}
                            `} role="status" aria-label={`Status: ${(sourceObject as any).status || 'Unknown'} - ${getStatusDescription((sourceObject as any).status)}`} key={`status-${(sourceObject as any).url}-${(sourceObject as any).status}`}>
                            <div className="bg-white rounded-full mr-[2px] -ml-[2px]">
                              <ProvenanceIcon status={(sourceObject as any).status} size={24} className={`box-border h-[20px] w-[20px] min-w-[20px] min-h-[20px] max-h-[20px] max-w-[20px]
                                  ${getStatusColor((sourceObject as any).status)}
                                  ${isStatus((sourceObject as any).status, 'SUCCESS') ? 'border-2 border-green-300 rounded-md' : isStatus((sourceObject as any).status, 'ERROR') ? 'border-2 border-red-300 rounded-md' : isStatus((sourceObject as any).status, 'INTERMEDIARY') ? 'border-2 border-yellow-300 rounded-md' : 'border-2 border-gray-300 rounded-md'}
                                `} aria-hidden="true" />
                            </div>
                          </div>
                        </Tooltip>}
                        <Popover placement='right-end' showArrow={false} shouldFlip={true} offset={isSmallScreen ? 4 : 8} crossOffset={isSmallScreen ? 0 : 0} autoFocus={true} className="outline-none" classNames={{
                  base: 'outline-none before:bg-gray-600 before:rounded-[2px] before:shadow-none before:drop-shadow-none border-0 before:w-[25px] before:h-[25px] mr-[-50px] sm:mr-0 h-screen sm:h-auto w-screen sm:w-auto ',
                  content: 'p-0'
                }} isOpen={previewPopoverOpen === index && isFilterVisible && !isFullscreen} onKeyDown={e => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSetPreviewPopoverOpen(-1);
                  }
                }}>
                          <PopoverTrigger>
                            <button type="button" aria-label={`Preview source ${index + 1}`} aria-haspopup="dialog" className="flex items-center justify-center w-[20px] h-[20px] text-white/65 cursor-pointer bg-transparent border-none p-0" onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      if ((sourceObject as any).url && useMetaViewer) {
                        const caseParam = caseId && caseId.trim() !== '' ? `&caseId=${encodeURIComponent(caseId)}` : '';
                        window.open(`/meta-viewer?s3Key=${encodeURIComponent(s3Key as any)}${caseParam}`, '_blank');
                      } else {
                        handleSetPreviewPopoverOpen(index);
                      }
                    }}>
                              <EyeIcon size={20} weight={hoveredIconIndex === index ? "fill" : "regular"} className="text-black/80 h-[20px] w-[20px] min-w-[20px] min-h-[20px] transition-all duration-200" onMouseEnter={() => setHoveredIconIndex(index)} onMouseLeave={() => setHoveredIconIndex(null)} />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="bg-[#2C3A4A] w-full sm:w-auto max-w-[100vw] sm:max-w-[520px] h-full sm:h-auto max-h-[100vh] sm:max-h-[95vh]">
                            <div ref={previewPopoverRef} className="z-50 w-full sm:w-auto max-w-[100vw] sm:max-w-[500px] max-h-[100vh] sm:max-h-[75vh] h-full sm:h-auto overflow-y-auto custom-scrollbar2">
                              <SourceCard sources={[{
                        sourceUrl: sourceObject.url,
                        sourceFilePath: sourceObject.url
                      }]} selectedSourceIndex={0} answerIndex={0} epochTimeSecs={0} handleDownload={handleDownloadWithIntercomFlag} handleAddSourceToScope={source => {
                        setPreviewPopoverOpen(-1);
                        addSourceToScope(source.sourceFilePath || '');
                      }} handleCopySourceForTimeline={handleCopySourceForTimeline} handleCopyToMediaShare={mediaSourceShare?.addToMediaShare ? (source: Source, _index: number, onSuccess?: () => void) => mediaSourceShare.addToMediaShare(source, onSuccess) : undefined} setIsExpanded={setIsExpanded} fullScreenSource={{
                        sourceUrl: sourceObject.url,
                        sourceFilePath: sourceObject.url
                      }} copiedSource={copiedSource} addedSource={addedSource} closeSources={() => handleSetPreviewPopoverOpen(-1)} addToTimeline={addToTimeline} caseId={caseId} />
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>;
          })}
            </div>
            <div ref={addSourcesButtonRef} className="p-0 m-0 pt-2 mt-2 flex justify-end border-t-3 border-brand-blue-light/50 border-double">
              {(selectedEvidence?.length > 0 || selectedScopes?.length > 0 || excludedEvidence?.length > 0 || excludedScopes?.length > 0) && <button className="px-4 py-2 text-white cursor-pointer rounded-md hover:bg-brand-blue/80 transition-colors duration-200
                    bg-brand-blue" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            handleAddScopesAndFiles({
              scopesToAdd: selectedScopes,
              filesToAdd: selectedEvidence,
              excludedScopesToAdd: excludedScopes,
              excludedFilesToAdd: excludedEvidence
            });
          }}>
                  Add {(selectedEvidence?.length || 0) + (selectedScopes?.length || 0) + (excludedEvidence?.length || 0) + (excludedScopes?.length || 0)} Selected
                </button>}
            </div>
          </div>
        </div> : <>
          {loadingEvidence || loadingScopes ? <div className="flex items-center justify-center h-full">
              <div className="animate-pulse font-bold pt-3">Loading Sources...</div>
            </div> : (filesSearchTerm && filesSearchTerm.trim().length > 2 || Object.keys(activeFileTypes).filter(key => activeFileTypes[key] === true).length > 0) && <div className="text-sm text-center py-4">
              No results match your search.
            </div>}
        </>}
    </>;
  return <>
      {contentCategoriesEnabled && typeof document !== 'undefined' && isSourceCardPopoverOpen && createPortal(<div className="fixed inset-0 z-[40] bg-black/50 backdrop-blur-sm" aria-hidden="true" onClick={() => handleSetPreviewPopoverOpen(-1)} />, document.body)}
      <Walkthru walkthruName="intro1" index={1} placement="left" width="300px" data-sentry-element="Walkthru" data-sentry-source-file="question-bar.tsx">
    <form ref={formRef} onSubmit={e => handleSubmit(e, false)} className={`box-border flex flex-col z-[2] items-center justify-center gap-1 m-0 p-0 mb-0 transition-all duration-500 ease-in-out outline-none
                ${hasChatHistory ? `fixed ${featureFlags?.enableMediaAndSourceShare ? 'bottom-[10px] sm:bottom-[50px]' : 'bottom-0'} left-1/2 transform -translate-x-1/2` : ''}
                ${isExpanded ? 'w-11/12 sm:w-6/12' : 'w-[60px]'}
                ${!hasChatHistory && isExpanded ? 'relative' : ''}
            `} autoComplete='off'>
      <div className={`box-border flex flex-row justify-center items-center gap-2 bottom-[0px] z-50 ${!isFilterVisible || !isExpanded ? 'max-w-[1px] max-h-[1px] overflow-hidden opacity-0' : ''} ${hasChatHistory ? 'bottom-[132px]' : ''}`}>
        <div ref={evidenceLockerRef} className={`bg-white text-black rounded-lg max-w-[95vw] border-[3px] border-[#99C4F6] border-solid ${contentCategoriesEnabled ? 'w-[920px]' : 'w-[800px]'}`}>
          <div className="flex flex-row justify-between rounded-t-sm items-center text-black/90 border-b-[3px] border-[#99C4F6] border-solid bg-brand-blue">
            <label className="text-white text-base px-2 py-1 flex flex-row justify-center items-center gap-2">
              <LockersIcon size={20} weight="regular" data-sentry-element="LockersIcon" data-sentry-source-file="question-bar.tsx" />
              Evidence Locker
            </label>
            <XIcon size={20} weight="regular" className="btn-h p-0 cursor-pointer text-white/65 hover:text-white mr-1" onClick={e => {
                e.stopPropagation();
                setIsFilterVisible(false);
                setPreviewPopoverOpen(-1);
                setFullscreenMediaItem(null);
              }} data-sentry-element="XIcon" data-sentry-source-file="question-bar.tsx" />
          </div>
          {contentCategoriesEnabled ? <div ref={evidenceLockerContentRef} className="flex min-w-0 flex-col gap-4 p-4 w-full">
              <div className="flex min-w-0 max-w-full flex-row items-center justify-center gap-[10px] w-[calc(100%-32px)]">
                <div className="box-border flex flex-row flex-1 justify-between items-center p-2 bg-white text-black/90 rounded-md cursor-pointer border-[2px] border-brand-blue-light border-solid bg-brand-blue-light/20 h-[36px]" onClick={e => {
                  e.stopPropagation();
                  const input = e.currentTarget.querySelector('input');
                  input?.focus();
                }}>
                  <input type="text" aria-label="Search evidence and scopes" className="box-border text-[14px] w-full outline-none bg-transparent placeholder:text-black/70" placeholder="Type at least 3 characters to filter evidence locker…" value={filesInputValue || ''} onChange={e => {
                    setEvidenceInputValue(e.target.value);
                    setScopeInputValue(e.target.value);
                  }} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }} />
                  <ListMagnifyingGlassIcon size={20} />
                </div>
                <ScopeDropdown selectedContentCategories={selectedContentCategories} onToggleCategory={category => {
                  setSelectedContentCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
                }} />
              </div>
              <div className="flex min-w-0 max-w-full flex-col gap-4 pt-4 px-4 pb-2 bg-[#f3f3f3] rounded-lg w-[calc(100%-64px)] h-[110px]">
                <span className="text-[14px] text-black tracking-[-0.21px] sm:whitespace-nowrap">Select file types to filter evidence locker:</span>
                <div className="filter-bar-scroll-desktop-hover min-w-0 -mx-1 touch-pan-x sm:mx-0">
                <div ref={filterBarRowRef} className="flex w-max flex-row flex-nowrap items-center gap-2 pr-1 pb-2 sm:w-full sm:min-w-0 sm:pr-0">
                  <FilterButton isActive={showFolders} onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleShowFolders();
                    }} icon={FolderOpenIcon} label="Folders" variant="expanded" />
                  <div className="flex w-[3px] h-[12px] rounded-full bg-[#d9d9d9] shrink-0"></div>
                  <FilterButton isActive={allFileTypes} onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleAllFileTypes();
                    }} icon={FilesIcon} label="All Files" variant="expanded" />
                  <div className="flex w-[3px] h-[12px] rounded-full bg-[#d9d9d9] shrink-0"></div>
                  {Object.keys(fileTypeControllers).map(key => {
                      const Icon = fileTypeIcons[key as keyof typeof fileTypeIcons];
                      return <FilterButton key={key} isActive={activeFileTypes[key]} onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleFileType(key);
                      }} icon={Icon} label={key} variant="expanded" />;
                    })}
                </div>
                </div>
              </div>
              {renderResultsArea("w-[calc(100%-32px)] flex flex-col gap-2")}
          </div> : <div className="flex items-center p-2">
            <div className="box-border flex flex-col p-2 w-full">
              <div className="flex flex-col">
                <div className="flex flex-row justify-start items-start sm:gap-2 gap-1 text-sm mb-2">
                  <div className="flex flex-row justify-start items-center sm:gap-2 gap-1 w-full scrollbar-hide sm:overflow-x-hidden overflow-x-auto">
                    <FilterButton isActive={showFolders} onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleShowFolders();
                      }} icon={FolderOpenIcon} label="Folders" />
                    <div className="flex w-[1px] sm:w-[3px] h-[12px] rounded-full bg-black/15"></div>
                    <div className="flex flex-row items-center sm:gap-2 gap-1">
                      {Object.keys(fileTypeControllers).map(key => {
                          const Icon = fileTypeIcons[key as keyof typeof fileTypeIcons];
                          return <FilterButton key={key} isActive={activeFileTypes[key] || selectedContentCategories.includes(key)} onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleFileType(key);
                          }} icon={Icon} label={key} />;
                        })}
                      <div className="flex w-[3px] h-[12px] rounded-full bg-black/15"></div>
                      <FilterButton isActive={allFileTypes} onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleAllFileTypes();
                        }} icon={FilesIcon} label="All Files" />
                    </div>
                  </div>
                </div>
                <div className="box-border flex flex-row justify-between items-center p-2 bg-white text-black/90 rounded-md cursor-pointer border-[2px] border-brand-blue-light border-solid bg-brand-blue-light/20 w-full" onClick={e => {
                    e.stopPropagation();
                    const input = e.currentTarget.querySelector('input');
                    input?.focus();
                  }}>
                  <input type="text" aria-label="Search evidence and scopes" className="box-border text-[14px] w-full outline-none bg-transparent placeholder:text-black/70" placeholder="Search for a file or folder…" value={filesInputValue || ''} onChange={e => {
                      setEvidenceInputValue(e.target.value);
                      setScopeInputValue(e.target.value);
                    }} onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }} />
                  <ListMagnifyingGlassIcon size={20} />
                </div>
              </div>
              {renderResultsArea("w-full border border-white/20 rounded-sm mt-3")}
            </div>
          </div>}
        </div>
      </div>
      <motion.div transition={{
          duration: 0.5
        }} className="box-border rounded-[32px] flex flex-row items-start w-full shadow-lg p-2 border-[1px]
          border-solid border-white/50 min-h-[60px] max-h-[200px] sm:pl-2 outline-none justify-between bg-[#2d8dfe] relative" onClick={e => {
          const textareaValue = textareaRef.current?.value?.trim() || '';
          if (textareaValue) {
            e.stopPropagation();
          }
          textareaRef.current?.focus();
          setIsFilterVisible(false);
          setPreviewPopoverOpen(-1);
          setFullscreenMediaItem(null);
        }} data-sentry-element="unknown" data-sentry-source-file="question-bar.tsx">
        {isExpanded && <div className="flex flex-col items-left justify-left gap-2">
          <Tooltip content={scopesAndFiles?.files && scopesAndFiles?.files.length > 0 || scopesAndFiles?.scopes && scopesAndFiles?.scopes.length > 0 || scopesAndFiles?.excludedFiles && scopesAndFiles?.excludedFiles.length > 0 || scopesAndFiles?.excludedScopes && scopesAndFiles?.excludedScopes.length > 0 ? "One click: Include or exclude more evidence. Two clicks: Remove all evidence from current search." : "Select evidence to include or exclude."} placement="left" delay={700} offset={4} showArrow={true} classNames={{
              content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
              base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
            }}>
            <button className={`cursor-pointer rounded-full w-fit p-2 flex flex-row items-center justify-center gap-1 ${isFilterVisible ? 'bg-white/30 text-white/100' : 'bg-white/5 border-white/20 text-white/70'} hover:bg-white/20 border-[1px] border-white/10 border-solid hover:border-white/10 hover:text-white/100 transition-all duration-300 ${!isExpanded ? 'hidden' : ''}`} data-testid="open-evidence-locker" aria-label={isFilterVisible ? "Close evidence filter" : "Open evidence filter"} onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                const {
                  files,
                  scopes
                } = scopesAndFiles || {
                  files: [],
                  scopes: []
                };
                if (files && files.length > 0 || scopes && scopes.length > 0) {
                  if (isFilterVisible) {
                    setScopesAndFiles({
                      scopes: [],
                      files: [],
                      excludedScopes: [],
                      excludedFiles: []
                    });
                    if (featureFlags?.contentCategories) {
                      setSelectedContentCategories([]);
                    }
                    setIsFilterVisible(false);
                    setPreviewPopoverOpen(-1);
                    setFullscreenMediaItem(null);
                  } else {
                    setIsFilterVisible(true);
                  }
                } else {
                  const newIsFilterVisible = !isFilterVisible;
                  if (newIsFilterVisible) {
                    setPreviewPopoverOpen(-1);
                  }
                  setIsFilterVisible(newIsFilterVisible);
                }
              }}>
              {scopesAndFiles?.files && scopesAndFiles?.files.length > 0 || scopesAndFiles?.scopes && scopesAndFiles?.scopes.length > 0 ? <FunnelXIcon size={25} /> : <FunnelIcon size={25} />}
              {/* <div className="text-xs whitespace-nowrap">Scope</div> */}
            </button>
          </Tooltip>
          {featureFlags?.promptLibrary && featureFlagsProp?.promptLibrary && (featureFlagsProp.promptLibrary as unknown as {
              Category: string;
              Prompt: string;
              Purpose: string;
              ShortPrompt: string;
              SubCategory: string;
            }[]).length > 0 && <Tooltip content="View prompt suggestions." placement="left" delay={700} offset={4} showArrow={true} classNames={{
              content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
              base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
            }}>
              <div>
                <PromptLibrary featureFlags={featureFlagsProp} onPromptSelect={handlePromptSelect} onOpenChange={setIsPromptLibraryOpen} isOpen={isPromptLibraryOpen} popoverRef={promptLibraryRef} placement="top-start" triggerElement={<button id="prompt-library-open-button" aria-label={isPromptLibraryOpen ? "Close prompt library" : "Open prompt library"} className={`cursor-pointer rounded-full p-2 border-[1px] border-solid transition-all duration-300 flex flex-row items-center justify-center gap-1 ${isExpanded ? 'flex' : 'hidden'} ${isPromptLibraryOpen ? 'bg-white/20 border-white/10 text-white/100' : 'bg-white/5 border-white/10 text-white/70 hover:bg-white/20 hover:border-white/10 hover:text-white/100'}`}>
                      <LightbulbFilamentIcon size={25} />
                    </button>} />
              </div>
            </Tooltip>}
        </div>}
        <motion.div animate={{
            width: isExpanded ? '100%' : '1px'
          }} transition={{
            width: {
              duration: 0.3
            },
            opacity: {
              duration: 0.2,
              delay: isExpanded ? 0 : 0.2
            }
          }} className={`box-border flex flex-row items-start justify-start ${isFullyExpanded ? 'mx-2 border-1 border-solid border-white/20 bg-white/5 rounded-[20px]' : ''}`} data-sentry-element="unknown" data-sentry-source-file="question-bar.tsx">
          <textarea ref={textareaRef} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} placeholder={isSmallScreen ? '' : isExpanded && localIsLoading ? 'Loading answers...' : !isExpanded && hasChatHistory ? '' : isFullyExpanded ? scopesAndFiles?.files && scopesAndFiles?.files.length > 0 || scopesAndFiles?.scopes && scopesAndFiles?.scopes.length > 0 || scopesAndFiles?.excludedFiles && scopesAndFiles?.excludedFiles.length > 0 || scopesAndFiles?.excludedScopes && scopesAndFiles?.excludedScopes.length > 0 ? 'Ask a question about this case (it will be scoped to the evidence listed below).' : 'Ask a question about this case.' : ''} aria-label="Ask a question" className={`box-border flex ${isExpanded ? 'w-[calc(100%-30px)] sm:mx-[20px] mx-[5px] sm:my-[10px] my-[5px] max-h-[150px] min-h-[85px] sm:min-h-[72px] sm:max-h-[160px] custom-scrollbar2' : 'w-0 h-0 min-h-0 max-h-0 m-0 p-0 overflow-hidden opacity-0 invisible pointer-events-none scrollbar-hide'} place-content-center leading-[22px] sm:leading-[24px] bg-transparent text-[16px] font-inter font-light placeholder-white focus:placeholder-opacity-0 resize-none focus:outline-none text-white caret-white transition-opacity duration-1000 overflow-hidden sm:overflow-auto items-center ${localIsLoading ? 'placeholder:animate-pulse' : ''}`} onKeyDown={handleKeyDown} disabled={localIsLoading} rows={1} />
        </motion.div>
        <div className={`flex flex-col gap-2 items-center justify-center`}>
          <button data-testid="AssistantQuery" aria-label={localIsLoading ? "Submitting question" : filterIsTooBig ? "Too many items selected" : "Submit question"} ref={assistantQueryButtonRef} type="submit" className={`box-border flex items-center justify-center bg-transparent rounded-full h-full m-0 p-0 ${filterIsTooBig ? 'hover:cursor-not-allowed opacity-50' : 'hover:cursor-pointer'}`} disabled={localIsLoading || filterIsTooBig} onClick={event => {
              event.stopPropagation();
              recordStat('AssistantQueryButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
              handleExpandClick(event);
            }} onMouseOver={handleExpandClick}>
            {localIsLoading === true ? <CircleDashedIcon size={43} weight="bold" className="box-border animate-spin bg-[#152539] rounded-full -ml-[1px] mr-[1px]" /> : <>
                  {filterIsTooBig ? <Tooltip classNames={{
                  content: 'bg-gray-800 text-white rounded-md border-0 border-transparent drop-shadow-none inner-shadow-none shadow-none px-1.5 py-1 w-[200px] text-center',
                  base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0'
                }} content="You have selected too many items. Please remove some files or use folders to submit your question." delay={0} showArrow={true} offset={0} portalContainer={barRef.current && barRef.current !== null ? barRef.current : document.body}>
                      <div className={`w-[32px] h-[32px] rounded-full border-[1px] border-solid transition-colors duration-300 hover:bg-white/20
                          ${isExpanded && !hasText ? "bg-white/10" : hasText ? "bg-white/20" : "bg-transparent"}
                          ${!isExpanded ? "border-white/0 bg-transparent px-2 py-1 ml-[-7.5px]" : "border-white/20 p-2"}
                        `}>
                        <Image className="w-full h-full" alt="Submit question" src="/icons/assistant.svg" width="32" height="32" />
                      </div>
                    </Tooltip> : <div className={`w-[32px] h-[32px] rounded-full border-[1px] border-solid transition-colors duration-300 hover:bg-white/30 ${isExpanded && !hasText ? "bg-white/10" : hasText ? "bg-white/20" : "bg-transparent"} ${!isExpanded ? "border-white/0 bg-transparent px-2 py-1 ml-[-18px] sm:ml-[-4px]" : "border-white/20 p-2"}`}>
                      <Image className="w-full h-full" alt="Submit question" src="/icons/assistant.svg" width="32" height="32" />
                    </div>}
                </>}
          </button>
          {featureFlags?.showPreliminaryResult && <Tooltip content={<div className="flex flex-col items-start justify-start gap-1">
                  DeepDive
                  <div className="text-white/40 text-xs">Takes a bit longer, but delivers more detailed results.</div>
                </div>} placement="right" delay={700} offset={4} showArrow={true} classNames={{
              content: 'bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
              base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0 w-[180px]'
            }}>
              <button aria-label={strandsModeQuerySent ? "DeepDive query sent" : "Send DeepDive query"} className={`cursor-pointer rounded-full w-[25px] h-[25px] p-[2px] bg-white/10 hover:bg-black border-[1px] border-solid border-white/20 flex flex-row items-center justify-center ${strandsModeQuerySent ? 'text-green-500' : 'text-white'} hover:text-white/100 transition-all duration-300 ${!isFullyExpanded ? 'hidden' : ''}`} onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit(e as unknown as React.FormEvent, true);
              }}>
                {strandsModeQuerySent ? <div className="box-border flex flex-row items-center justify-center gap-1 bg-white rounded-full p-1 w-[25px] h-[25px]">
                    <CheckIcon size={20} weight="bold" className="text-green-500" />
                  </div> : <CrosshairIcon size={25} weight="regular" />}
              </button>
            </Tooltip>}
        </div>
      </motion.div>
      {(scopesAndFiles?.files && scopesAndFiles?.files.length > 0 || scopesAndFiles?.scopes && scopesAndFiles?.scopes.length > 0 || scopesAndFiles?.excludedFiles && scopesAndFiles?.excludedFiles.length > 0 || scopesAndFiles?.excludedScopes && scopesAndFiles?.excludedScopes.length > 0 || contentCategoriesEnabled && selectedContentCategories && selectedContentCategories.length > 0) && isExpanded && <div ref={selectedItemsRef} className="box-border bg-[#99C4F6] rounded-b-3xl w-full px-2 pb-2 pt-[28px] mt-[-28px]" style={{
          width: formRef.current?.offsetWidth
        }}>
          <div ref={barRef} className="max-h-[142px] overflow-y-auto custom-scrollbar2 box-border flex flex-wrap items-center justify-start gap-2 mt-[-4px] pt-2">
            {contentCategoriesEnabled && selectedContentCategories.length > 0 && selectedContentCategories.map((fileType, index) => {
              const Icon = fileTypeIcons[fileType as keyof typeof fileTypeIcons];
              return <div key={`filetype-${index}`} className="flex flex-row justify-center items-center gap-1 rounded-full pl-1 pr-2 pt-[3px] pb-0 text-white/40 border-[1px] border-solid border-brand-blue/70 hover:bg-brand-blue/70 bg-brand-blue/20">
                  <button onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeContentCategory(fileType);
                }} className="hover:text-black transition-colors duration-200 cursor-pointer">
                    <XCircleIcon size={24} weight="regular" />
                  </button>
                  <div className="flex flex-row items-center justify-center gap-1 cursor-default">
                    <Icon size={20} weight="regular" className="text-white/80" />
                    <label className="text-white/100 text-sm pb-[2.5px]">
                      {fileType}
                    </label>
                  </div>
                </div>;
            })}
            {formRef.current && formRef.current !== null && [...[...scopesAndFiles?.scopes, ...scopesAndFiles?.excludedScopes].sort((a: string, b: string) => a.localeCompare(b)), ...[...scopesAndFiles?.files, ...scopesAndFiles?.excludedFiles].sort((a: string, b: string) => a.localeCompare(b))].map((fileOrScope, index) => <Tooltip key={index} content={<CopyTooltipContent fileOrScope={fileOrScope} scopesAndFiles={scopesAndFiles} />} delay={300} showArrow={true} offset={0} portalContainer={barRef.current && barRef.current !== null ? barRef.current : document.body} className='flex flex-row gap-1 items-start justify-start max-w-[350px] bg-gray-700' classNames={{
              content: 'p-3 text-wrap bg-tooltip-bg text-white px-3 py-2 rounded-md text-sm',
              base: 'before:bg-tooltip-bg before:rounded-[2px] before:shadow-none before:drop-shadow-none before:border-0 max-w-[350px]'
            }}>
                <div key={index} className={`
                    flex flex-row justify-center items-center gap-1 rounded-full pl-1 pr-2 pt-[3px] pb-0 text-white/40
                    border-[1px] border-solid relative
                    ${scopesAndFiles?.excludedFiles?.includes(fileOrScope) || scopesAndFiles?.excludedScopes?.includes(fileOrScope) ? 'border-red-600/50 hover:bg-red-600/70 bg-red-600/10' : 'border-[#2d8dfe]/50 hover:bg-[#2d8dfe]/70'}
                  `} onMouseEnter={() => setHoveredItem(fileOrScope)} onMouseLeave={() => setHoveredItem(null)}>
                  <button aria-label={`Remove ${scopesAndFiles?.scopes?.includes(fileOrScope) ? 'scope' : 'file'}: ${removeMD5Hash(fileOrScope.split('/').pop() || '')}`} onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  removeScopeOrFile(fileOrScope);
                }} className="hover:text-black transition-colors duration-200 cursor-pointer">
                    <XCircleIcon size={24} weight="regular" />
                  </button>
                  <div className="flex flex-row items-center justify-center gap-1 cursor-default">
                    {scopesAndFiles?.scopes?.includes(fileOrScope) && <FolderOpenIcon size={24} weight="regular" className="text-white/80 mt-[-5px]" />}
                    <label className="text-white/100 text-sm pb-[2.5px]">
                      {removeMD5Hash(fileOrScope.split('/').pop() || '')}
                    </label>
                    {hoveredItem === fileOrScope && getHoverButtonStyle(fileOrScope)}
                  </div>
                </div>
              </Tooltip>)}
          </div>
        </div>}
      {hasChatHistory && <>
          <div className="flex flex-row items-center justify-center whitespace-nowrap">
            <Button className="flex flex-row items-center justify-center text-white/40 hover:text-white/80 bg-[#0D1D31] rounded-full pl-2 pr-[12px] py-0 m-0 border-[1px] border-white/15 border-solid hover:border-white/45 hover:bg-black hover:bg-opacity-100 hover:opacity-100 transition-colors duration-300 my-3 cursor-pointer gap-2" onPress={() => {
              setFullscreenMediaItem(null);
              setPreviewPopoverOpen(-1);
              setIsFilterVisible(false);
              setIsExpanded(false);
              setIsAlertOpen(true);
            }}>
              <ArrowClockwiseIcon size={20} />
              Start New Session
            </Button>
          </div>
          <Alert title="Confirm Reset" description="Are you sure you want to start a new session?" hideIcon={false} isVisible={isAlertOpen} onVisibleChange={visible => {
            if (!visible) {
              setIsAlertOpen(false);
            }
          }} classNames={{
            base: 'bg-black text-black/40 absolute bottom-[-10px] left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-start justify-start w-fit h-fit border-white/20 border-solid border-[1px] shadow-lg w-[290px] h-[120px] pb-4',
            iconWrapper: 'bg-blue-400',
            mainWrapper: 'flex flex-col items-start justify-start',
            title: 'text-blue-400 font-bold text-lg',
            description: 'text-white italic'
          }}>
            <div className="flex flex-row items-start justify-start gap-2 mt-4">
              <Button id="CancelReset" title="Cancel" className="cursor-pointer bg-white/10 text-white/65" onPress={(event: any) => {
                if (event?.stopPropagation) {
                  event.stopPropagation();
                }
                setIsAlertOpen(false);
                recordStat('CancelResetButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
              }}>
                Cancel
              </Button>
              <Button id="ResetConversationHistory" title="Reset Session" onPress={(event: any) => {
                if (event?.stopPropagation) {
                  event.stopPropagation();
                }
                onClearThread();
                setEvidenceInputValue('');
                recordStat('ResetConversationHistoryButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
              }} disabled={isResetting} className="cursor-pointer bg-white/20 text-white/80 border-white/20 border-solid border-[1px] flex items-center gap-2">
                {isResetting ? <>
                    Starting Session
                    <span className="flex">
                      <span className="animate-[bounce_1s_infinite_0ms]">.</span>
                      <span className="animate-[bounce_1s_infinite_200ms]">.</span>
                      <span className="animate-[bounce_1s_infinite_400ms]">.</span>
                    </span>
                  </> : 'Start New Session'}
              </Button>
            </div>
          </Alert>
        </>}
    </form>
      </Walkthru>
    </>;
};